import { RouteLocationNormalized, Router } from 'vue-router';
import { getItemFromSessionStorageAsBoolean } from './web-storage';
import { Link } from 'types/layout';
import { redirect } from './client';
import { useAccountSetupChecklistStore } from '@stores/account-setup-checklist';

/**
 * Determines whether a path is part of the Sign-Up flow by checking against known Sign-Up route roots.
 * @param path
 */
export const isSignUpRoute = (path: string): boolean => {
	const roots = [
		'questionnaire',
		'question',
		'signup',
		'info',
		'reits/signup-checkout',
		'reits/checkout',
		'ira/start',
		'ira/checkout'
	];
	return roots.some((root) => path.startsWith(`/${root}/`));
};

export const handleRouting = async (link: Link, router: Router): Promise<void> => {
	if (link.router) {
		try {
			await router.push({ name: link.router, query: link.query, params: link.params });
		} catch {
			// this is to avoid an annoying error in the current version of Vue Router and the creator of Vue Router explicitly told people to do this - Jake Kenneally
			// https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
		}
	} else if (link.href) {
		redirect(link.href);
	} else {
		throw new Error('Routing error');
	}
};

export const stripRouteHash = (route: RouteLocationNormalized): string => {
	const fullPath = route.fullPath;
	const hashIndex = fullPath.indexOf('#');
	const queryIndex = fullPath.indexOf('?');
	if (hashIndex > -1) {
		if (queryIndex > hashIndex) {
			return fullPath.substring(0, hashIndex) + fullPath.substring(queryIndex, fullPath.length);
		}
		return fullPath.substring(0, hashIndex);
	}
	return fullPath;
};

export const checkChecklistRedirect = async (
	router: Router,
	defaultRoute: string,
	isLastChecklistItem = false,
	routeParams?: Record<string, string>,
	routeQuery?: Record<string, string>
): Promise<void> => {
	const accountSetupChecklistStore = useAccountSetupChecklistStore();

	const isChecklistRedirect = getItemFromSessionStorageAsBoolean('redirect-to-checklist');
	if (isChecklistRedirect) {
		/** If the user just completed the final outstanding checklist task,
		 * enable the success state so the checklist page can display the
		 * one-time "Completed" state upon redirect. This state is cleared immediately after the checklist is shown.
		 */
		if (isLastChecklistItem) {
			accountSetupChecklistStore.showSuccessState = true;
		}
		await router.push({ name: 'profile-account-setup-checklist' });
	} else {
		await router.push({ name: defaultRoute, params: routeParams, query: routeQuery });
	}
};

export const getRouteHash = (route: RouteLocationNormalized): string => {
	return route?.hash?.split('#')[1] || '';
};

export const isValidSlug = (slug: string): boolean => {
	return /^[a-zA-Z0-9]{1,100}$/.test(slug);
};
